import "../../../datagrid.scss";
import { useContext, useEffect, useState, useRef, useMemo } from 'react';
import { Modal, Box } from "@mui/material";
import FormDetalle from './formDetalle/formDetalle';
import { ClientesZonasState } from '../../../../context/clienteszonas/ClientesZonasState';
import { ClientesZonasFormContext } from '../../../../context/clienteszonas/ClientesZonasContext';
import { ClientesState } from '../../../../context/clientes/ClientesState';
import { formatRows } from "./configGrid/rows";
import headers from "./configGrid/headers";
import { indice } from "../../../../components/DataGrid/indice";
import { getActionColumn } from "../../../../components/DataGrid/actionColumns";
import CustomDataGrid from "../../../../components/DataGrid/CustomDataGrid";
import ConfirmDialog from "../../../../components/dialogs/DeleteDialog";
import ErrorDialog from "../../../../components/dialogs/ErrorDeleteDialog";
import { getDataGrid, deleteDataGrid } from './configGrid/apiCalls';
import FiltersGrid from './filtersGrid/filtersGrid';
import { Paginator } from '../../../../components/paginator/Paginator';

export const DatatableDetalle = ({
  zonaId,            // ID de la zona de entrega (cabecera)
  arrayDetalle,      // Array temporal de items
  addItem,
  getItem,
  updateItem,
  removeItem,
  clearArray,
  isCabComplete,
}) => {

  // Modales
  const [isModalDetalleOpen, setIsModalDetalleOpen] = useState(false);

  // Estado para Datos y Paginación
  const [listaItems, setListaItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  // Estado para Loading
  const [isLoading, setIsLoading] = useState(false);

  // Paginación
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [numPagina, setNumPagina] = useState(1);
  const [filas, setFilas] = useState(isMobile ? 20 : 50);

  // Buscador
  const [searchTerm, setSearchTerm] = useState('');

  // Refs
  const isFetchingRef = useRef(false);

  // Modal de confirmación para eliminar
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Manejo de errores
  const [errorMessage, setErrorMessage] = useState("");
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);

  const nameData = "Cliente";

  // ═══════════════════════════════════════════════════════════════════════════
  // Cargar datos
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    fetchData();
  }, [zonaId, searchTerm, numPagina, filas, JSON.stringify(arrayDetalle)]);

  const fetchData = async () => {
    if (isFetchingRef.current || !zonaId) return;

    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      // Obtener datos de la API
      const result = await getDataGrid(zonaId, searchTerm, numPagina, filas);

      if (result && result.clientes_zonas) {
        // Combinar datos de API con el array temporal
        const apiData = result.clientes_zonas || [];
        const tempData = arrayDetalle.filter(item => item.isNew);

        // Filtrar duplicados del array temporal que ya existan en API
        const combinedData = [
          ...apiData,
          ...tempData.filter(temp => !apiData.some(api => api.user_id === temp.user_id))
        ];

        setListaItems(combinedData);
        setTotalItems(result.pagination?.totalItems || combinedData.length);
      } else {
        // Solo mostrar datos del array temporal
        setListaItems(arrayDetalle);
        setTotalItems(arrayDetalle.length);
      }
    } catch (error) {
      console.error("ERROR fetchData:", error);
      // Si falla la API, mostrar solo array temporal
      setListaItems(arrayDetalle);
      setTotalItems(arrayDetalle.length);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Acciones
  // ═══════════════════════════════════════════════════════════════════════════

  // Editar cliente
  const handleClickEdit = async (id) => {
    try {
      const item = listaItems.find(i => i.id === id);
      setSelectedItem(item?.id || id);
      handleOpenDetalleModal();
    } catch (error) {
      console.error("Error al obtener detalles:", error);
    }
  };

  // Eliminar cliente de la zona
  const handleDelete = async () => {
    try {
      const item = listaItems.find(i => i.id === selectedItem);

      if (item?.isNew) {
        // Si es nuevo (aún no guardado), solo remover del array
        removeItem(selectedItem);
      } else {
        // Si existe en la BD, eliminar via API
        await deleteDataGrid(selectedItem);
      }

      await fetchData();
      closeModal();
    } catch (error) {
      console.error(error);
      setErrorMessage("No se pudo eliminar el elemento. Inténtalo nuevamente.");
      setIsErrorDialogOpen(true);
    }
  };

  const closeErrorDialog = () => {
    setErrorMessage("");
    setIsErrorDialogOpen(false);
  };

  const openModal = (id) => {
    setSelectedItem(id);
    setIsModalOpen(true);
  };

  const closeModal = async () => {
    await fetchData();
    setSelectedItem(null);
    setIsModalOpen(false);
  };

  const handleOpenDetalleModal = () => {
    setIsModalDetalleOpen(true);
  };

  const handleCloseDetalleModal = async () => {
    await fetchData();
    setIsModalDetalleOpen(false);
    setSelectedItem(null);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Columnas para el DataGrid
  // ═══════════════════════════════════════════════════════════════════════════
  const datosColum = useMemo(() => {
    return indice(numPagina, filas)
      .concat(headers.filter((column) => !column.hidden))
      .concat(getActionColumn(handleClickEdit, openModal));
  }, [numPagina, filas]);

  const rows = useMemo(() => {
    return formatRows(listaItems);
  }, [listaItems]);

  return (
    <>
      <div className="datatable">
        <div className="datatableTitle">
          👥 Clientes asignados a esta Zona

          {zonaId && (
            <FiltersGrid
              setSearchFilter={setSearchTerm}
            />
          )}

          <button
            type="button"
            className={`link ${!isCabComplete ? "disabled" : "enabled"}`}
            onClick={handleOpenDetalleModal}
            disabled={!isCabComplete}
          >
            + Agregar Cliente
          </button>
        </div>

        <CustomDataGrid
          rows={rows}
          columns={datosColum}
          pageSize={filas}
          rowCount={totalItems}
          onPageChange={(newPage) => setNumPagina(newPage)}
          onPageSizeChange={(newPageSize) => {
            setFilas(newPageSize);
            setNumPagina(1);
          }}
          isLoading={isLoading}
          enablePagination={false}
        />

        <div className="paginatorWrapper">
          <Paginator
            numPagina={numPagina}
            setNumPagina={setNumPagina}
            totalPages={Math.ceil(totalItems / filas)}
            filas={filas}
            setFilas={setFilas}
            totalDataItems={totalItems}
          />
        </div>

        <ConfirmDialog
          open={isModalOpen}
          onClose={closeModal}
          onConfirm={handleDelete}
          nameData={nameData}
        />
        <ErrorDialog
          open={isErrorDialogOpen}
          onClose={closeErrorDialog}
          errormessages={errorMessage}
        />
      </div>

      {/* Modal: Agregar/Editar un cliente */}
      <ClientesZonasState>
        <Modal
          open={isModalDetalleOpen}
          onClose={handleCloseDetalleModal}
          aria-labelledby="modal-form-detalle"
          className="modalLista"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxWidth: '700px',
              maxHeight: '90%',
              width: '95%',
              bgcolor: 'background.paper',
              borderRadius: '12px',
              boxShadow: 24,
              overflowY: 'auto',
            }}
          >
            <FormDetalle
              zonaId={zonaId}
              iddet={selectedItem}
              arrayDetalle={arrayDetalle}
              addItem={addItem}
              updateItem={updateItem}
              getItem={getItem}
              removeItem={removeItem}
              closeModal={handleCloseDetalleModal}
            />
          </Box>
        </Modal>
      </ClientesZonasState>
    </>
  );
};

export default DatatableDetalle;
